// Starts from the browser's saved copy (pinia/theme.ts) so the first paint is
// in the right accent.
import { queryOptions, useQuery } from "@tanstack/vue-query";
import { getActivePinia } from "pinia";
import { computed, reactive } from "vue";

import { getTheme } from "../api/portal";
import { useThemeStore, type AccentTheme } from "../pinia/theme";

export const themeKeys = {
  portal: ["theme"] as const,
};

// The accent changes only when an admin saves it, which invalidates this.
const THEME_FRESH_MS = 10 * 60 * 1000;

// Without Pinia, no saved copy is read or written.
export function themeQueryOptions() {
  const lastSeen = getActivePinia() ? useThemeStore() : null;
  return queryOptions({
    queryKey: themeKeys.portal,
    queryFn: async ({ signal }) => {
      const theme = await getTheme({ signal });
      if (lastSeen) {
        lastSeen.theme = theme;
        lastSeen.themeFetchedAt = Date.now();
      }
      return theme;
    },
    staleTime: THEME_FRESH_MS,
    initialData: () => lastSeen?.theme ?? undefined,
    initialDataUpdatedAt: () => lastSeen?.themeFetchedAt ?? 0,
  });
}

export function useTheme(): { readonly accent: AccentTheme; readonly highAccessibility: boolean } {
  const query = useQuery(themeQueryOptions());
  return reactive({
    accent: computed(() => (query.data.value?.Accent as AccentTheme | undefined) ?? "grape"),
    highAccessibility: computed(() => query.data.value?.HighAccessibility ?? false),
  });
}
